<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      :autoLoad="false"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useContas } from '../../composables/useContas';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';

type ContaId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellNode {
  rowPinned?: boolean;
}

interface CellParams {
  node?: CellNode;
  value?: unknown;
}

interface FormaPagamento {
  forma_pagamento_id: number;
  forma_pagamento_nome: string;
}

interface ValidationData {
  is_active?: boolean;
  is_complete?: boolean;
  issues?: string[];
}

withDefaults(defineProps<{
  usuarioVersao?: number;
}>(), {
  usuarioVersao: 0
});

const emit = defineEmits<{
  edit: [contaId: ContaId];
}>();

const { t } = useI18n();
const { getAccountTypeIcon, getAccountTypeName } = useContas();
const { initialized } = useFiltro();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed(() => '/index.php/contas/contas/lista_contas');

const tableColumns = computed(() => {
  return [
    {
      data: 'conta_id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'conta_nome',
      title: t('Nome da conta'),
      width: 200,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'conta_franquia_nome',
      title: t('Franquia'),
      width: 150,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'conta_tipo',
      title: t('Tipo'),
      width: 120,
      sortable: true,
      filterType: 'select',
      cellRenderer: (params: CellParams) => {
        if (params.node && params.node.rowPinned) {
          return params.value != null ? String(params.value) : '';
        }
        const icon = getAccountTypeIcon(params.value as string);
        const name = getAccountTypeName(params.value as string);
        return `<i class="pi ${icon}"></i> ${name}`;
      }
    },
    {
      data: 'formas_pagamento',
      title: t('Formas de Pagamento'),
      width: 200,
      sortable: false,
      cellRenderer: (params: CellParams) => {
        if (params.node && params.node.rowPinned || !params.value) {
          return '';
        }
        const formas = (params.value as FormaPagamento[]) || [];
        if (formas.length === 0) return '-';
        const tags = formas.map(forma =>
          `<span class="p-tag p-tag-default" style="margin-right: 3px;">${forma.forma_pagamento_nome}</span>`
        ).join('');
        return tags;
      }
    },
    {
      data: 'usuarios',
      title: t('Usuários'),
      width: 100,
      sortable: false,
      cellRenderer: (params: CellParams) => {
        if (params.node && params.node.rowPinned || !params.value) {
          return '';
        }
        const users = (params.value as unknown[]) || [];
        return `<i class="pi pi-users"></i> ${users.length}`;
      }
    },
    {
      data: 'conta_status',
      title: t('Status'),
      width: 100,
      sortable: true,
      filterType: 'select',
      cellRenderer: (params: CellParams) => {
        if (params.node && params.node.rowPinned) {
          return params.value != null ? String(params.value) : '';
        }
        if (params.value == 1) {
          return '<span class="p-tag p-tag-success">'+t('Ativo')+'</span>';
        } else {
          return '<span class="p-tag p-tag-danger">'+t('Inativo')+'</span>';
        }
      }
    },
    {
      data: 'validation',
      title: t('Saúde'),
      width: 120,
      sortable: false,
      cellRenderer: (params: CellParams) => {
        if (params.node && params.node.rowPinned || !params.value) {
          return '';
        }
        const validation = params.value as ValidationData;

        // Generate tag based on validation
        let tagType = 'secondary';
        let tagText = t('Desconhecido');
        let tagIcon = 'pi-question';
        let tooltip = t('Status desconhecido');

        if (!validation.is_active) {
          tagType = 'danger';
          tagText = t('Inativa');
          tagIcon = 'pi-times-circle';
          tooltip = t('Conta inativa');
        } else if (validation.is_complete) {
          tagType = 'success';
          tagText = t('Completa');
          tagIcon = 'pi-check-circle';
          tooltip = t('Conta totalmente configurada');
        } else {
          tagType = 'warning';
          tagText = t('Incompleta');
          tagIcon = 'pi-exclamation-triangle';
          const issues = validation.issues || [];
          const tooltipParts = [];
          if (issues.includes('missing_payment_methods')) {
            tooltipParts.push(t('faltam formas de pagamento'));
          }
          if (issues.includes('missing_users')) {
            tooltipParts.push(t('nenhum usuário atribuído'));
          }
          tooltip = t('Conta precisa de atenção: ') + tooltipParts.join(', ');
        }

        return `<span class="p-tag p-tag-${tagType}" title="${tooltip}" style="margin-left: 5px;">
          <i class="pi ${tagIcon}" style="margin-right: 3px;"></i>${tagText}
        </span>`;
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `contas_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: { conta_id?: ContaId }) => {
      const id = data?.conta_id;
      return id == null ? '' : `contas-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV')
}));

const loadData = (): void => {
  log('ContasTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ContasTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de contas'));
};

const handleDataError = (error: Error): void => {
  log('ContasTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar contas: ') + error.message);
};

const editItem = (contaId: ContaId): void => {
  log('ContasTable', 'editItem', 'Editing ID', contaId);
  emit('edit', contaId);
};

const handleRowClick = (rowData: { conta_id?: ContaId } | null | undefined): void => {
  log('ContasTable', 'handleRowClick', 'Row clicked', rowData?.conta_id || null);
};

const handleEditClick = (contaId: ContaId): void => {
  log('ContasTable', 'handleEditClick', 'Edit clicked for ID', contaId);
  editItem(contaId);
};

// Named handlers for proper cleanup (defined at component level for cleanup access)
const handleFilterUpdated = (): void => {
  log('ContasTable', 'event:filter.updated', 'Received');
  loadData();
};

const handleContasSaved = (): void => {
  log('ContasTable', 'event:contas.saved', 'Received');
  loadData();
};

const handleContasDeleted = (): void => {
  log('ContasTable', 'event:contas.deleted', 'Received');
  loadData();
};

onMounted(() => {
  log('ContasTable', 'mounted', 'Component mounted');
  log('ContasTable', 'mounted', 'Filter initialized status:', initialized.value);

  // Always trigger initial load
  log('ContasTable', 'mounted', 'Triggering initial data load');
  loadData();

  // Listen for filter updates
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('contas.saved', handleContasSaved);
  eventBus.on('contas.deleted', handleContasDeleted);
});

onBeforeUnmount(() => {
  log('ContasTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('contas.saved', handleContasSaved);
  eventBus.off('contas.deleted', handleContasDeleted);
});

defineExpose({
  loadData
});
</script>
